import { createStore } from "zustand/vanilla";
import { errorToMessage } from "../errorToMessage.js";
export const createAuthStore = (deps) => {
    const deviceTimezone = deps.deviceTimezone ?? (() => Intl.DateTimeFormat().resolvedOptions().timeZone);
    // Runs an auth operation, mirrors its outcome into the store's `error` field
    // (cleared on success), and *also* returns the typed Result. There's a single
    // await here — the promise isn't split. The point is to centralize the one
    // side-effect every action shares (write the error message to store state) in
    // one place instead of repeating `set({ error })` in each action, while still
    // handing the caller the typed Result so a screen can branch on success/failure
    // itself (e.g. navigate, close a modal) without reading it back out of the store.
    // `setError` is named to distinguish it from zustand's own `set` below.
    const failable = async (setError, run) => {
        const r = await run();
        setError(r.ok ? null : errorToMessage(r.error));
        return r;
    };
    const store = createStore((set) => ({
        status: "loading",
        user: null,
        error: null,
        signUp: (email, password, displayName) => failable((error) => set({ error }), async () => {
            const r = await deps.auth.signUp(email, password, displayName);
            // Persist the signup defaults (device timezone, 7am reminder hour —
            // Doc 09 §1). Best-effort: a failed profile write must not undo a
            // successful signup; the settings streams fall back to defaults.
            if (r.ok) {
                await deps.profiles.writeProfile({ timezone: deviceTimezone(), reminderSendHour: 7 });
                // Best-effort, same reasoning: get the confirmation link on its way so
                // listMyInvites (T25) works without a trip to Settings first. A federated
                // signup is already emailVerified, so there's nothing to send.
                if (!r.value.emailVerified)
                    await deps.auth.sendEmailVerification();
            }
            return r;
        }),
        signIn: (email, password) => failable((error) => set({ error }), () => deps.auth.signIn(email, password)),
        signInAnonymously: () => failable((error) => set({ error }), () => deps.auth.signInAnonymously()),
        signInWithProvider: (provider) => failable((error) => set({ error }), () => deps.auth.signInWithProvider(provider)),
        // Link/unlink return the updated user, mirrored into state directly: Firebase's
        // auth-state stream does not re-fire when providerData changes.
        linkProvider: (provider) => failable((error) => set({ error }), async () => {
            const r = await deps.auth.linkProvider(provider);
            if (r.ok)
                set({ user: r.value });
            return r;
        }),
        unlinkProvider: (providerId) => failable((error) => set({ error }), async () => {
            const r = await deps.auth.unlinkProvider(providerId);
            if (r.ok)
                set({ user: r.value });
            return r;
        }),
        // Same mirroring as link/unlinkProvider: a profile change doesn't re-fire the
        // auth-state stream, so the returned user goes into state directly.
        setAuthDisplayName: (displayName) => failable((error) => set({ error }), async () => {
            const r = await deps.auth.setAuthDisplayName(displayName);
            if (r.ok)
                set({ user: r.value });
            return r;
        }),
        signOut: () => deps.auth.signOut(),
        changeEmail: (newEmail, currentPassword) => failable((error) => set({ error }), () => deps.auth.changeEmail(newEmail, currentPassword)),
        changePassword: (currentPassword, newPassword) => failable((error) => set({ error }), () => deps.auth.changePassword(currentPassword, newPassword)),
        // Like link/unlink: providers changed, mirror the returned user into state.
        setPassword: (newPassword) => failable((error) => set({ error }), async () => {
            const r = await deps.auth.setPassword(newPassword);
            if (r.ok)
                set({ user: r.value });
            return r;
        }),
        sendPasswordReset: (email) => failable((error) => set({ error }), () => deps.auth.sendPasswordReset(email)),
        sendEmailVerification: () => failable((error) => set({ error }), () => deps.auth.sendEmailVerification()),
        // Like link/unlinkProvider: mirror the refreshed user into state directly, since
        // the auth-state stream doesn't re-fire on its own once the link is clicked.
        refreshCurrentUser: () => failable((error) => set({ error }), async () => {
            const r = await deps.auth.refreshCurrentUser();
            if (r.ok)
                set({ user: r.value });
            return r;
        }),
    }));
    // Subscribe to the authoritative auth state; this is what flips the router.
    deps.auth.observeCurrentUser().subscribe((user) => {
        store.setState({ status: user ? "signedIn" : "signedOut", user });
    });
    return store;
};
//# sourceMappingURL=authStore.js.map